import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Textarea,
  Select,
  Input,
  Flex,
  Text,
  IconButton,
  useDisclosure,
  HStack,
  VStack,
  Skeleton,
  Tooltip,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/react";
import {
  EditIcon,
  DeleteIcon,
  ViewIcon,
  SearchIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getDataSuperAdmin,
  updateDataSuperAdmin,
  deleteDataSuperAdmin,
} from "../../../redux/actions/common.action";
import { useNavigate } from "react-router-dom";
import Pagination from "../../proposal/components/Pagination ";
import {
  Filter,
  RefreshCcw,
  MessageSquare,
  ArrowLeft,
  Home,
  Mail,
} from "lucide-react";
import DeleteConfirmationModal from "../../../components/DeleteConfirmationModal";

interface ContactEnquiry {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  enquiryType: string;
  message: string;
  adminComment: string;
  createdAt: string;
}

const ContactEnquiryList: React.FC = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const navigate = useNavigate();

  const {
    contactEnquiries = [],
    contactEnquiryPage = {
      currentPage: 1,
      totalPages: 1,
      totalDocs: 0,
      limit: 10,
      hasNextPage: false,
      hasPrevPage: false,
    },
    loading,
  } = useSelector((state: any) => state.common);

  const [selectedEnquiry, setSelectedEnquiry] = useState<ContactEnquiry | null>(
    null
  );
  const [adminComment, setAdminComment] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [enquiryTypeFilter, setEnquiryTypeFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [deleteEnquiry, setDeleteEnquiry] = useState<{
    id: string;
    name: string;
    email: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    isOpen: isCommentModalOpen,
    onOpen: onCommentModalOpen,
    onClose: onCommentModalClose,
  } = useDisclosure();

  const {
    isOpen: isViewModalOpen,
    onOpen: onViewModalOpen,
    onClose: onViewModalClose,
  } = useDisclosure();

  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();

  useEffect(() => {
    fetchEnquiries();
  }, [currentPage, pageLimit]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchEnquiries();
    }, 600);

    return () => clearTimeout(timer);
  }, [searchTerm, enquiryTypeFilter]);

  const fetchEnquiries = async () => {
    let queryParams = `contact-enquiry?page=${currentPage}&limit=${pageLimit}`;

    if (enquiryTypeFilter) {
      queryParams += `&enquiryType=${enquiryTypeFilter}`;
    }

    if (searchTerm) {
      queryParams += `&search=${searchTerm}`;
    }

    await dispatch(getDataSuperAdmin(queryParams) as any);
  };

  const handleUpdateComment = async () => {
    if (!selectedEnquiry) return;

    const updateData = {
      adminComment,
    };

    try {
      await dispatch(
        updateDataSuperAdmin(
          selectedEnquiry._id,
          "contact-enquiry",
          updateData,
          navigate,
          toast
        ) as any
      );
      onCommentModalClose();
      setAdminComment("");
      fetchEnquiries();

      toast({
        title: "Success",
        description: "Enquiry updated successfully",
        status: "success",
        duration: 3000,
        position: "top-right",
        isClosable: true,
      });
    } catch (error) {
      console.error("Error updating enquiry:", error);
    }
  };

  const handleDeleteClick = (enquiry: ContactEnquiry) => {
    setDeleteEnquiry({
      id: enquiry._id,
      name: `${enquiry.firstName} ${enquiry.lastName}`,
      email: enquiry.email,
    });
    onDeleteModalOpen();
  };

  const confirmDelete = async () => {
    if (!deleteEnquiry) return;

    setIsDeleting(true);
    try {
      await dispatch(
        deleteDataSuperAdmin(deleteEnquiry.id, "contact-enquiry", toast) as any
      );
      fetchEnquiries();
      onDeleteModalClose();
      setDeleteEnquiry(null);

      toast({
        title: "Deleted Successfully",
        description: "Contact enquiry has been deleted",
        status: "success",
        duration: 3000,
        position: "top-right",
        isClosable: true,
      });
    } catch (error) {
      console.error("Error deleting enquiry:", error);
      toast({
        title: "Error",
        description: "Failed to delete enquiry",
        status: "error",
        duration: 3000,
        position: "top-right",
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setDeleteEnquiry(null);
      onDeleteModalClose();
    }
  };

  const openCommentModal = (enquiry: ContactEnquiry) => {
    setSelectedEnquiry(enquiry);
    setAdminComment(enquiry.adminComment || "");
    onCommentModalOpen();
  };

  const openViewModal = (enquiry: ContactEnquiry) => {
    setSelectedEnquiry(enquiry);
    onViewModalOpen();
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text?.length <= maxLength) return text;
    return text?.substring(0, maxLength) + "...";
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageLimitChange = (newLimit: number) => {
    setPageLimit(newLimit);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setEnquiryTypeFilter("");
    setCurrentPage(1);
    fetchEnquiries();
  };

  const getPaginationInfo = () => {
    if (
      !contactEnquiries ||
      contactEnquiries.length === 0 ||
      !contactEnquiryPage?.totalDocs
    ) {
      return "Showing 0 to 0 of 0 enquiries";
    }
    const startItem = (currentPage - 1) * pageLimit + 1;
    const endItem = Math.min(
      currentPage * pageLimit,
      contactEnquiryPage.totalDocs
    );
    return `Showing ${startItem} to ${endItem} of ${contactEnquiryPage.totalDocs} enquiries`;
  };

  return (
    <Box p="8px" bg="gray.50">
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .refresh-icon-spin {
            animation: spin 1s linear infinite;
          }
          @keyframes slideInLeft {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          .slide-in-left {
            animation: slideInLeft 0.4s ease-out;
          }
        `}
      </style>
      {/* Modern Back Navigation Header */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        mb="8px"
        px={{ base: 3, md: 4 }}
        py={{ base: 2, md: 3 }}
        className="slide-in-left"
      >
        <Flex
          justify="space-between"
          align="center"
          flexWrap="wrap"
          gap={{ base: 2, md: 4 }}
        >
          {/* Left: Back Button + Breadcrumb */}
          <Flex
            flexDirection={{ base: "row", lg: "column" }}
            align={{ base: "center", lg: "flex-start" }}
            gap={{ base: 2, lg: 1 }}
          >
            {/* Responsive Back Button */}
            <Button
              leftIcon={<ArrowLeft size={16} />}
              onClick={() => navigate("/crm-super-admin-dashboard")}
              size={{ base: "xs", sm: "sm" }}
              variant="ghost"
              colorScheme="blue"
              fontWeight="medium"
              fontSize={{ base: "xs", sm: "sm" }}
              px={3}
              _hover={{
                bg: "blue.50",
                transform: "translateX(-4px)",
              }}
              transition="all 0.2s"
            >
              {/* Hide text on small screens */}
              <Box display={{ base: "none", sm: "inline" }}>
                Back to Dashboard
              </Box>
            </Button>

            {/* Breadcrumb Navigation */}
            <Breadcrumb
              spacing={2}
              separator={<ChevronRightIcon color="gray.400" boxSize={3} />}
              fontSize={{ base: "xs", md: "sm" }}
              color="gray.600"
              display={{ base: "none", sm: "flex" }}
            >
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={() => navigate("/crm-super-admin-dashboard")}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  _hover={{ color: "blue.500" }}
                  transition="color 0.2s"
                >
                  <Home size={12} />
                  <Text>Dashboard</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink
                  display="flex"
                  alignItems="center"
                  gap={1}
                  color="blue.500"
                  fontWeight="medium"
                >
                  <Mail size={12} />
                  <Text>Contact Enquiries</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
          </Flex>

          {/* Right: Page Title and Stats */}
          <HStack spacing={{ base: 2, md: 4 }} align="center">
            <VStack align="end" spacing={0}>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                fontWeight="bold"
                color="gray.800"
              >
                Contact Enquiries
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                Manage customer messages – Total {contactEnquiries?.length}
              </Text>
            </VStack>
            <Box
              bgGradient="linear(to-br, orange.400, orange.600)"
              p={{ base: 2, md: 3 }}
              borderRadius="xl"
              boxShadow="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Mail size={20} color="white" />
            </Box>
          </HStack>
        </Flex>
      </Box>

      {/* Search and Filter Header */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        mb="8px"
      >
        <Flex
          mt={4}
          px={4}
          pb={4}
          justify="space-between"
          align="center"
          gap={2}
          flexDirection={{ base: "column", lg: "row" }}
        >
          <Box position="relative" maxW="md">
            <SearchIcon
              position="absolute"
              left="12px"
              top="50%"
              transform="translateY(-50%)"
              color="gray.400"
              w={4}
              h={4}
            />
            <Input
              w={{ base: "300px", lg: "400px" }}
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              pl="40px"
              pr={4}
              py={2}
              fontSize="xs"
              border="1px"
              borderColor="gray.300"
              borderRadius="lg"
              _focus={{
                outline: "none",
                ring: 1,
                ringColor: "blue.500",
                borderColor: "blue.500",
              }}
            />
          </Box>

          <HStack spacing={2}>
            <Button
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              fontSize="xs"
              fontWeight="medium"
              color="gray.700"
              bg="white"
              border="1px"
              borderColor="gray.300"
              _hover={{ bg: "gray.50" }}
              px={3}
              py={2}
              borderRadius="lg"
              leftIcon={<Filter size={12} />}
            >
              Filters
            </Button>
            <Button
              size="sm"
              onClick={handleClearFilters}
              isDisabled={loading}
              fontSize="xs"
              fontWeight="medium"
              color="gray.700"
              bg="white"
              border="1px"
              borderColor="gray.300"
              _hover={!loading ? { bg: "gray.50" } : {}}
              px={3}
              py={2}
              borderRadius="lg"
              title="Refresh data and clear filters"
            >
              <Box
                as={RefreshCcw}
                size={14}
                mr={2}
                className={loading ? "refresh-icon-spin" : ""}
              />
              Refresh
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Filters Panel */}
      {showFilters && (
        <Box
          p={4}
          bg="gray.50"
          border="1px"
          borderColor="gray.200"
          borderRadius="lg"
          mb="8px"
        >
          <HStack spacing={3}>
            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
                Type
              </Text>
              <Select
                value={enquiryTypeFilter}
                onChange={(e) => setEnquiryTypeFilter(e.target.value)}
                fontSize="xs"
                border="1px"
                borderColor="gray.300"
                borderRadius="md"
                px={2}
                py={1}
                size="sm"
                _focus={{
                  outline: "none",
                  ring: 1,
                  ringColor: "blue.500",
                }}
              >
                <option value="">All Types</option>
                <option value="new">New to Matrix</option>
                <option value="existing">Existing User</option>
              </Select>
            </Box>
          </HStack>
        </Box>
      )}

      {/* Table */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
      >
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg="gray.50" borderBottom="1px" borderColor="gray.200">
              <Tr>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Name
                </Th>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Email
                </Th>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Phone
                </Th>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Type
                </Th>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Message
                </Th>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Date
                </Th>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Actions
                </Th>
              </Tr>
            </Thead>
            <Tbody borderTop="1px" borderColor="gray.200">
              {loading ? (
                [...Array(pageLimit)].map((_, index) => (
                  <Tr key={index}>
                    {[...Array(7)].map((_, cellIndex) => (
                      <Td key={cellIndex} px={3} py={3}>
                        <Skeleton height="16px" borderRadius="md" />
                      </Td>
                    ))}
                  </Tr>
                ))
              ) : contactEnquiries?.length > 0 ? (
                contactEnquiries.map((enquiry: ContactEnquiry) => (
                  <Tr
                    key={enquiry._id}
                    _hover={{ bg: "gray.50" }}
                    borderBottom="1px"
                    borderColor="gray.200"
                  >
                    <Td px={3} py={3}>
                      <Text fontSize="xs" fontWeight="medium" color="gray.900">
                        {enquiry.firstName} {enquiry.lastName}
                      </Text>
                    </Td>
                    <Td px={3} py={3}>
                      <Text fontSize="xs" color="gray.700">
                        {enquiry.email}
                      </Text>
                    </Td>
                    <Td px={3} py={3}>
                      <Text fontSize="xs" color="gray.700">
                        {enquiry.phone}
                      </Text>
                    </Td>
                    <Td px={3} py={3}>
                      <Badge
                        colorScheme={
                          enquiry.enquiryType === "new" ? "purple" : "teal"
                        }
                        fontSize="xs"
                        px={2}
                        py={1}
                        borderRadius="full"
                        fontWeight="medium"
                      >
                        {enquiry.enquiryType === "new" ? "NEW" : "EXISTING"}
                      </Badge>
                    </Td>
                    <Td px={3} py={3} maxW="200px">
                      <Tooltip label={enquiry.message} placement="top">
                        <Text
                          fontSize="xs"
                          color="gray.700"
                          noOfLines={2}
                          whiteSpace={"nowrap"}
                        >
                          {truncateText(enquiry.message, 40)}
                        </Text>
                      </Tooltip>
                    </Td>
                    <Td px={3} py={3}>
                      <Text
                        fontSize="xs"
                        color="gray.700"
                        whiteSpace={"nowrap"}
                      >
                        {new Date(enquiry.createdAt).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </Text>
                    </Td>
                    <Td px={3} py={3}>
                      <HStack spacing={1}>
                        <IconButton
                          aria-label="View"
                          icon={<ViewIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => openViewModal(enquiry)}
                          p="6px"
                          minW="auto"
                          h="auto"
                          _hover={{ bg: "blue.50" }}
                          title="View Details"
                        />
                        <IconButton
                          aria-label="Edit"
                          icon={<EditIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="orange"
                          onClick={() => openCommentModal(enquiry)}
                          p="6px"
                          minW="auto"
                          h="auto"
                          _hover={{ bg: "orange.50" }}
                          title="Update Enquiry"
                        />
                        <IconButton
                          aria-label="Delete"
                          icon={<DeleteIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDeleteClick(enquiry)}
                          p="6px"
                          minW="auto"
                          h="auto"
                          _hover={{ bg: "red.50" }}
                          title="Delete Enquiry"
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={7} px={3} py={16} textAlign="center">
                    <VStack spacing={4}>
                      <MessageSquare size={64} color="#d1d5db" />
                      {searchTerm || enquiryTypeFilter ? (
                        <VStack spacing={2}>
                          <Text
                            fontSize="lg"
                            fontWeight="medium"
                            color="gray.700"
                          >
                            No enquiries found
                          </Text>
                          <VStack spacing={2}>
                            <Text fontSize="sm" color="gray.500">
                              No results match your search criteria
                            </Text>
                            <HStack spacing={2}>
                              <Button
                                onClick={() => {
                                  setSearchTerm("");
                                  fetchEnquiries();
                                }}
                                size="xs"
                                variant="link"
                                colorScheme="blue"
                              >
                                Clear search
                              </Button>
                              <Text fontSize="xs" color="gray.400">
                                or
                              </Text>
                              <Button
                                onClick={handleClearFilters}
                                size="xs"
                                variant="link"
                                colorScheme="blue"
                              >
                                Reset all filters
                              </Button>
                            </HStack>
                          </VStack>
                        </VStack>
                      ) : (
                        <VStack spacing={6}>
                          <Text
                            fontSize="lg"
                            fontWeight="medium"
                            color="gray.700"
                          >
                            No enquiries yet
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            Contact enquiries from your website will appear here
                          </Text>
                        </VStack>
                      )}
                    </VStack>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>

        {/* Pagination */}
        <Flex
          justify="space-between"
          align="center"
          px={2}
          py={2}
          flexDirection={{ base: "column", lg: "row" }}
          gap={2}
        >
          <HStack spacing={4}>
            <Text fontSize="xs" color="gray.600" fontWeight="medium">
              {getPaginationInfo()}
            </Text>
          </HStack>

          <Flex flexDirection={{base:"column", md:"row"}} align={"center"} justifyItems={"center"}>
            <HStack spacing={2}>
              <Text fontSize="xs" color="gray.600" fontWeight="medium">
                Show
              </Text>
              <Select
                value={pageLimit}
                onChange={(e) =>
                  handlePageLimitChange(parseInt(e.target.value))
                }
                size="xs"
                bg="white"
                border="1px"
                borderColor="gray.300"
                borderRadius="md"
                px={3}
                py={1}
                fontSize="xs"
                fontWeight="medium"
                color="gray.700"
                _hover={{ borderColor: "gray.400" }}
                _focus={{
                  outline: "none",
                  ring: 1,
                  ringColor: "blue.500",
                  borderColor: "blue.500",
                }}
                w="100px"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Select>
              <Text fontSize="xs" color="gray.600">
                per page
              </Text>
            </HStack>

            {contactEnquiryPage?.totalPages > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={contactEnquiryPage.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </Flex>
        </Flex>
      </Box>

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={onViewModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md" fontWeight="semibold">
            Enquiry Details
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedEnquiry && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="bold" fontSize="xs" color="gray.600" mb={1}>
                    Name
                  </Text>
                  <Text fontSize="sm">
                    {selectedEnquiry.firstName} {selectedEnquiry.lastName}
                  </Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" fontSize="xs" color="gray.600" mb={1}>
                    Email
                  </Text>
                  <Text fontSize="sm">{selectedEnquiry.email}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" fontSize="xs" color="gray.600" mb={1}>
                    Phone
                  </Text>
                  <Text fontSize="sm">{selectedEnquiry.phone}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" fontSize="xs" color="gray.600" mb={1}>
                    Type
                  </Text>
                  <Badge
                    colorScheme={
                      selectedEnquiry.enquiryType === "new" ? "purple" : "teal"
                    }
                    fontSize="xs"
                    px={2}
                    py={1}
                    borderRadius="md"
                  >
                    {selectedEnquiry.enquiryType === "new"
                      ? "New to Matrix"
                      : "Existing User"}
                  </Badge>
                </Box>
                <Box>
                  <Text fontWeight="bold" fontSize="xs" color="gray.600" mb={1}>
                    Message
                  </Text>
                  <Box
                    p={3}
                    bg="gray.50"
                    borderRadius="md"
                    whiteSpace="pre-wrap"
                    fontSize="sm"
                  >
                    {selectedEnquiry.message}
                  </Box>
                </Box>
                {selectedEnquiry.adminComment && (
                  <Box>
                    <Text
                      fontWeight="bold"
                      fontSize="xs"
                      color="gray.600"
                      mb={1}
                    >
                      Admin Comment
                    </Text>
                    <Box
                      p={3}
                      bg="blue.50"
                      borderRadius="md"
                      whiteSpace="pre-wrap"
                      fontSize="sm"
                    >
                      {selectedEnquiry.adminComment}
                    </Box>
                  </Box>
                )}
                <Box>
                  <Text fontWeight="bold" fontSize="xs" color="gray.600" mb={1}>
                    Submitted On
                  </Text>
                  <Text fontSize="sm">
                    {new Date(selectedEnquiry.createdAt).toLocaleString()}
                  </Text>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onViewModalClose} size="sm" fontSize="xs">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Comment/Update Modal */}
      <Modal
        isOpen={isCommentModalOpen}
        onClose={onCommentModalClose}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md" fontWeight="semibold">
            Update Enquiry
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedEnquiry && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="bold" fontSize="xs" color="gray.600" mb={2}>
                    Customer Details
                  </Text>
                  <Box p={3} bg="gray.50" borderRadius="md">
                    <Text fontSize="xs">
                      <strong>Name:</strong> {selectedEnquiry.firstName}{" "}
                      {selectedEnquiry.lastName}
                    </Text>
                    <Text fontSize="xs" mt={1}>
                      <strong>Email:</strong> {selectedEnquiry.email}
                    </Text>
                    <Text fontSize="xs" mt={1}>
                      <strong>Phone:</strong> {selectedEnquiry.phone}
                    </Text>
                  </Box>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="xs" color="gray.600" mb={2}>
                    Customer Message
                  </Text>
                  <Box
                    p={3}
                    bg="gray.50"
                    borderRadius="md"
                    maxH="150px"
                    overflowY="auto"
                    whiteSpace="pre-wrap"
                    fontSize="xs"
                  >
                    {selectedEnquiry.message}
                  </Box>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="xs" color="gray.600" mb={2}>
                    Admin Comment
                  </Text>
                  <Textarea
                    value={adminComment}
                    onChange={(e) => setAdminComment(e.target.value)}
                    placeholder="Add your comment here..."
                    rows={6}
                    resize="vertical"
                    fontSize="xs"
                  />
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button
                variant="outline"
                size="sm"
                onClick={onCommentModalClose}
                fontSize="xs"
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                size="sm"
                onClick={handleUpdateComment}
                isDisabled={!adminComment}
                fontSize="xs"
              >
                Update Enquiry
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={confirmDelete}
        title="Delete Contact Enquiry"
        itemName={deleteEnquiry?.name || ""}
        itemType="contact enquiry"
        description={`This enquiry from ${deleteEnquiry?.email} will be removed from the list.`}
        isDeleting={isDeleting}
        warningMessage="This action will soft delete the enquiry. It will be hidden from the list but can be restored by an administrator if needed."
        confirmButtonText="Delete Enquiry"
        cancelButtonText="Keep It"
      />
    </Box>
  );
};

export default ContactEnquiryList;
